"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { surfaceFrom } from "@/components/optilase/surface";
import { statsFrom, type StatsRef } from "@/lib/global-stats";
import { cn } from "@/lib/utils";

type LpStat = SbBlokData & { value?: string; label?: string };

type LpStatsBlok = SbBlokData & {
  heading?: string;
  stats?: LpStat[];
  stats_data?: StatsRef;
  bg_color?: unknown;
};

export default function LpStats({ blok }: { blok: LpStatsBlok }) {
  const stats = statsFrom(blok.stats_data, blok.stats);
  if (stats.length === 0) return null;

  const surface = surfaceFrom(blok.bg_color);

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(
        "px-5 py-12 text-white md:px-10 lg:px-20 lg:py-16",
        surface ? "" : "bg-teal-08",
      )}
    >
      <div className="mx-auto w-full max-w-7xl">
        {blok.heading && (
          <h2 className="text-section mb-10 max-w-205 text-white lg:mb-14">{blok.heading}</h2>
        )}

        <dl className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
          {stats.map((stat) => (
            <div
              key={stat._uid as string}
              {...storyblokEditable(stat)}
              className="flex flex-col gap-2 border-t border-white/20 pt-5"
            >
              <dt className="sr-only">{stat.label}</dt>
              <dd className="text-h2 leading-none font-bold">{stat.value}</dd>
              <p className="text-base leading-snug text-white/80">{stat.label}</p>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
